import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, LinearProgress, CircularProgress,
  Paper,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "";

const D = {
  bg:        "#0a0a0f",
  surface:   "#111118",
  border:    "#1e1e2e",
  borderMed: "#2a2a3e",
  accent:    "#7c6aff",
  accentSoft:"rgba(124,106,255,0.12)",
  text:      "#e8e8f0",
  textMuted: "#6b6b8a",
  success:   "#22c55e",
  danger:    "#ef4444",
  dangerSoft:"rgba(239,68,68,0.1)",
  font:      "'Inter', sans-serif",
};

const EBOOK_TYPES = [
  {
    value:   "authority_guide",
    label:   "Authority Guide",
    funnel:  "Top of Funnel",
    desc:    "Establishes brand as the definitive source. Best for lead capture and brand awareness.",
    icon:    "🏛️",
  },
  {
    value:   "problem_solution",
    label:   "Problem-Solution",
    funnel:  "Mid Funnel",
    desc:    "Names the pain precisely then delivers the clearest path out. Converts warm leads.",
    icon:    "🎯",
  },
  {
    value:   "case_study",
    label:   "Case Study / Proof",
    funnel:  "Bottom of Funnel",
    desc:    "Real transformation arc with concrete results. Built for near-decision prospects.",
    icon:    "📊",
  },
  {
    value:   "how_to_playbook",
    label:   "How-To Playbook",
    funnel:  "Retention",
    desc:    "Step-by-step system the reader can implement today. Deepens client relationships.",
    icon:    "📋",
  },
];

const LENGTH_OPTIONS = [
  { value: "short",         label: "Short",         detail: "15–20 pages · 3 chapters" },
  { value: "standard",      label: "Standard",      detail: "30–40 pages · 5 chapters" },
  { value: "comprehensive", label: "Comprehensive", detail: "50–60 pages · 7 chapters" },
];

const CTA_OPTIONS = [
  "Book a call",
  "Download another resource",
  "Join a programme",
  "Buy a product",
];

export default function EbookWizard() {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess]     = useState(null);
  const [topic, setTopic]             = useState("");
  const [ebookType, setEbookType]     = useState("authority_guide");
  const [length, setLength]           = useState("standard");
  const [primaryCta, setPrimaryCta]   = useState("Book a call");
  const [audience, setAudience]       = useState("");
  const [additionalCtx, setAdditionalCtx] = useState("");

  const [credits, setCredits]     = useState(0);
  const [jobId, setJobId]         = useState(null);
  const [status, setStatus]       = useState(null);
  const [pct, setPct]             = useState(0);
  const [jobMeta, setJobMeta]     = useState(null);
  const [error, setError]         = useState("");
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.data?.has_ebook) { setHasAccess(false); return; }
        setHasAccess(true);
      })
      .catch(() => navigate("/login"));
    axios.get(`${API}/api/ebook/credits`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCredits(r.data?.credits ?? 0))
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (!jobId || status === "complete" || status === "failed") return;
    pollRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await axios.get(`${API}/api/ebook/status/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStatus(r.data.status);
        setPct(r.data.pct || 0);
        if (r.data.status === "complete" || r.data.status === "failed") {
          setJobMeta(r.data);
          clearInterval(pollRef.current);
          setGenerating(false);
        }
      } catch { /* non-fatal */ }
    }, 8000);
    return () => clearInterval(pollRef.current);
  }, [jobId, status]);

  const handleGenerate = async () => {
    if (!topic.trim() || topic.trim().length < 10) {
      setError("Topic must be at least 10 characters.");
      return;
    }
    setError("");
    setGenerating(true);
    setStatus("queued");
    setPct(5);
    setJobMeta(null);
    try {
      const token = localStorage.getItem("token");
      const r = await axios.post(`${API}/api/ebook/generate`, {
        topic:              topic.trim(),
        ebook_type:         ebookType,
        length:             length,
        primary_cta:        primaryCta,
        audience:           audience.trim(),
        additional_context: additionalCtx.trim(),
      }, { headers: authHeaders() });
      setJobId(r.data.job_id);
      setCredits(r.data.credits_remaining);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to start generation.");
      setGenerating(false);
      setStatus(null);
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const r = await axios.get(`${API}/api/ebook/download/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url  = URL.createObjectURL(new Blob([r.data], { type: "text/markdown" }));
      const link = document.createElement("a");
      link.href     = url;
      link.download = jobMeta?.filename || "ebook.md";
      link.click();
      URL.revokeObjectURL(url);
    } catch { setError("Download failed. Please try again."); }
  };

  const handleReset = () => {
    setJobId(null); setStatus(null); setPct(0);
    setJobMeta(null); setGenerating(false); setError("");
  };

  if (hasAccess === null) return (
    <Box sx={{ minHeight: "100vh", bgcolor: D.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <CircularProgress sx={{ color: D.accent }} />
    </Box>
  );

  if (!hasAccess) {
    navigate("/home", { replace: true });
    return null;
  }

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: D.surface,
      color: D.text,
      fontFamily: D.font,
      fontSize: "14px",
      borderRadius: "10px",
      "& fieldset": { borderColor: D.border },
      "&:hover fieldset": { borderColor: D.borderMed },
      "&.Mui-focused fieldset": { borderColor: D.accent },
    },
    "& .MuiInputLabel-root": { color: D.textMuted, fontFamily: D.font, fontSize: "13px" },
    "& .MuiInputLabel-root.Mui-focused": { color: D.accent },
  };

  const selectedLengthDetail = LENGTH_OPTIONS.find(l => l.value === length)?.detail || "";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: D.bg, py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 680, mx: "auto" }}>

        {/* Header */}
        <Box sx={{ mb: 5 }}>
          <Button
            onClick={() => navigate("/")}
            sx={{ color: D.textMuted, fontFamily: D.font, textTransform: "none", fontSize: "13px", mb: 2, pl: 0, "&:hover": { color: D.text, bgcolor: "transparent" } }}
          >
            ← Back
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
            <Typography sx={{ fontSize: "26px", fontWeight: 700, color: D.text, fontFamily: D.font, letterSpacing: "-0.02em" }}>
              eBook Wizard
            </Typography>
            <Box sx={{
              px: 1.5, py: 0.5, borderRadius: "8px",
              background: credits > 0 ? D.accentSoft : D.dangerSoft,
              border: `1px solid ${credits > 0 ? "#7c6aff44" : "rgba(239,68,68,0.25)"}`,
              display: "flex", alignItems: "center", gap: 0.75,
            }}>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: credits > 0 ? D.accent : D.danger, fontFamily: D.font }}>
                📖 {credits} eBook credit{credits !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: "14px", color: D.textMuted, fontFamily: D.font, mt: 0.5 }}>
            Generate a full professional eBook from your topic. Gemini 2.5 Pro writes every chapter.
          </Typography>
        </Box>

        {/* Form */}
        {!jobId && (
          <Paper sx={{ bgcolor: D.surface, border: `1px solid ${D.border}`, borderRadius: "14px", p: 3.5, display: "flex", flexDirection: "column", gap: 3 }}>

            <TextField
              label="Topic / Focus"
              placeholder="e.g. How AI is changing content marketing for B2B brands"
              value={topic}
              onChange={e => setTopic(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={inputSx}
              helperText={
                <Typography component="span" sx={{ fontSize: "11px", color: topic.length < 10 ? D.danger : D.textMuted, fontFamily: D.font }}>
                  {topic.length < 10 ? `${10 - topic.length} more characters needed` : "Good — specific topics produce better eBooks"}
                </Typography>
              }
            />

            {/* eBook Type */}
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: D.textMuted, fontFamily: D.font, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                eBook Type
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                {EBOOK_TYPES.map(t => {
                  const selected = ebookType === t.value;
                  return (
                    <Box
                      key={t.value}
                      onClick={() => setEbookType(t.value)}
                      sx={{
                        p: 2, borderRadius: "10px", cursor: "pointer",
                        border: `1.5px solid ${selected ? D.accent : D.border}`,
                        background: selected ? D.accentSoft : "transparent",
                        transition: "all 0.15s",
                        "&:hover": { borderColor: selected ? D.accent : D.borderMed },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontSize: "16px" }}>{t.icon}</Typography>
                        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: selected ? D.accent : D.text, fontFamily: D.font }}>
                          {t.label}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: "10px", fontWeight: 600, color: selected ? D.accent : D.textMuted, fontFamily: D.font, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
                        {t.funnel}
                      </Typography>
                      <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font, lineHeight: 1.5 }}>
                        {t.desc}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Length + CTA row */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth sx={inputSx}>
                <InputLabel>Length</InputLabel>
                <Select
                  value={length}
                  onChange={e => setLength(e.target.value)}
                  label="Length"
                  sx={{ color: D.text, fontFamily: D.font, fontSize: "14px" }}
                  MenuProps={{ PaperProps: { sx: { bgcolor: D.surface, border: `1px solid ${D.border}`, borderRadius: "10px" } } }}
                >
                  {LENGTH_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value} sx={{ color: D.text, fontFamily: D.font, fontSize: "14px", "&:hover": { bgcolor: D.accentSoft } }}>
                      <Box>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: D.text, fontFamily: D.font }}>{o.label}</Typography>
                        <Typography sx={{ fontSize: "11px", color: D.textMuted, fontFamily: D.font }}>{o.detail}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={inputSx}>
                <InputLabel>Primary CTA</InputLabel>
                <Select
                  value={primaryCta}
                  onChange={e => setPrimaryCta(e.target.value)}
                  label="Primary CTA"
                  sx={{ color: D.text, fontFamily: D.font, fontSize: "14px" }}
                  MenuProps={{ PaperProps: { sx: { bgcolor: D.surface, border: `1px solid ${D.border}`, borderRadius: "10px" } } }}
                >
                  {CTA_OPTIONS.map(c => (
                    <MenuItem key={c} value={c} sx={{ color: D.text, fontFamily: D.font, fontSize: "14px", "&:hover": { bgcolor: D.accentSoft } }}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Target Audience (optional)"
              placeholder="e.g. Marketing directors at B2B SaaS companies"
              value={audience}
              onChange={e => setAudience(e.target.value)}
              fullWidth
              sx={inputSx}
            />

            <TextField
              label="Additional context (optional)"
              placeholder="e.g. Focus on practical frameworks. Avoid academic references."
              value={additionalCtx}
              onChange={e => setAdditionalCtx(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={inputSx}
            />

            {credits <= 0 && (
              <Box sx={{ p: 1.5, borderRadius: "9px", background: D.dangerSoft, border: "1px solid rgba(239,68,68,0.25)" }}>
                <Typography sx={{ fontSize: "13px", color: D.danger, fontFamily: D.font }}>
                  No eBook credits remaining. Contact support or purchase credits to generate.
                </Typography>
              </Box>
            )}

            {error && (
              <Typography sx={{ fontSize: "13px", color: D.danger, fontFamily: D.font }}>{error}</Typography>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: "13px", color: D.textMuted, fontFamily: D.font }}>
                Cost: <strong style={{ color: D.text }}>1 eBook credit</strong>
                {selectedLengthDetail && (
                  <span style={{ color: D.textMuted }}> · {selectedLengthDetail}</span>
                )}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: D.textMuted, fontFamily: D.font }}>
                Balance after: <strong style={{ color: credits > 1 ? D.accent : D.danger }}>{Math.max(0, credits - 1)} credits</strong>
              </Typography>
            </Box>

            <Button
              onClick={handleGenerate}
              disabled={topic.trim().length < 10 || credits <= 0}
              fullWidth
              sx={{
                bgcolor: D.accent, color: "#fff", fontFamily: D.font, fontWeight: 600,
                textTransform: "none", borderRadius: "10px", py: 1.5, fontSize: "15px",
                "&:hover": { bgcolor: "#6a58e8" },
                "&:disabled": { bgcolor: D.border, color: D.textMuted },
              }}
            >
              Generate eBook
            </Button>
          </Paper>
        )}

        {/* Progress state */}
        {jobId && status !== "complete" && status !== "failed" && (
          <Paper sx={{ bgcolor: D.surface, border: `1px solid ${D.border}`, borderRadius: "14px", p: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: D.text, fontFamily: D.font, mb: 1 }}>
              Writing your eBook…
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, fontFamily: D.font, mb: 3 }}>
              Gemini 2.5 Pro is generating your {EBOOK_TYPES.find(t => t.value === ebookType)?.label || "eBook"}. This takes 2–4 minutes.
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 6, borderRadius: 3, mb: 1,
                bgcolor: D.border,
                "& .MuiLinearProgress-bar": { bgcolor: D.accent, borderRadius: 3 },
              }}
            />
            <Typography sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.font }}>{pct}%</Typography>
          </Paper>
        )}

        {/* Complete state */}
        {status === "complete" && jobMeta && (
          <Paper sx={{ bgcolor: D.surface, border: `1px solid ${D.border}`, borderRadius: "14px", p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: D.success }} />
              <Typography sx={{ fontSize: "16px", fontWeight: 600, color: D.text, fontFamily: D.font }}>
                eBook ready
              </Typography>
            </Box>

            <Typography sx={{ fontSize: "22px", fontWeight: 700, color: D.text, fontFamily: D.font, mb: 0.5 }}>
              {jobMeta.title || topic}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, fontFamily: D.font, mb: 3 }}>
              {jobMeta.num_chapters} chapters · ~{jobMeta.word_count?.toLocaleString()} words
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={handleDownload}
                fullWidth
                sx={{
                  bgcolor: D.accent, color: "#fff", fontFamily: D.font, fontWeight: 600,
                  textTransform: "none", borderRadius: "10px", py: 1.4, fontSize: "14px",
                  "&:hover": { bgcolor: "#6a58e8" },
                }}
              >
                Download eBook (.md)
              </Button>
              <Button
                onClick={handleReset}
                sx={{
                  color: D.textMuted, fontFamily: D.font, textTransform: "none",
                  border: `1px solid ${D.border}`, borderRadius: "10px", px: 3,
                  "&:hover": { borderColor: D.borderMed, color: D.text },
                }}
              >
                New eBook
              </Button>
            </Box>
          </Paper>
        )}

        {/* Failed state */}
        {status === "failed" && (
          <Paper sx={{ bgcolor: D.dangerSoft, border: `1px solid ${D.danger}`, borderRadius: "14px", p: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: D.danger, fontFamily: D.font, mb: 1 }}>
              Generation failed
            </Typography>
            <Typography sx={{ fontSize: "13px", color: D.textMuted, fontFamily: D.font, mb: 2 }}>
              {jobMeta?.error || "An unexpected error occurred."}
            </Typography>
            <Button onClick={handleReset} sx={{ color: D.accent, fontFamily: D.font, textTransform: "none" }}>
              Try again
            </Button>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
